import { cs } from '../../../lib/currency';
import { Card, CardContent } from '../../ui/card';
import { Receipt, RefreshCw, AlertTriangle, CheckCircle, DollarSign } from 'lucide-react';

/**
 * Top KPIs for the Invoice Management page. All figures reflect the rows
 * currently loaded, which the page scopes to the selected duration.
 */
export default function InvoiceStats({ invoices, loading = false }) {
  // iter382 — "failed" auto-pay invoices are money still owed, so they
  // count as unpaid/overdue. Outstanding sums the OUTSTANDING balance
  // (partial invoices only owe their remainder), not the full amount.
  // iter385 — business rule: there is no "Pending" state in the accounting
  // views. Any issued, unpaid invoice is Overdue/Outstanding regardless of
  // due date. The due date is kept only for display + aging buckets.
  // iter385 — Outstanding uses the canonical server-stamped `open_balance`
  // (services.invoice_balance.open_balance_cents) so this card agrees to the
  // cent with the Aged AR report. Falls back to the old client calc only if
  // the field is absent (older cached payloads).
  const unpaidStatuses = ['pending', 'overdue', 'processing', 'partial', 'failed'];
  const outstandingBalance = (i) =>
    (i.open_balance != null)
      ? Number(i.open_balance || 0)
      : ((i.status === 'partial' && i.balance_due != null)
        ? Number(i.balance_due || 0)
        : Number(i.amount || 0));
  const stats = {
    total: invoices.length,
    processing: invoices.filter((i) => i.status === 'processing').length,
    // iter385 — Overdue now folds in the old "pending" rows: issued = overdue.
    overdue: invoices.filter((i) => ['pending', 'overdue', 'failed', 'partial'].includes(i.status)).length,
    paid: invoices.filter((i) => i.status === 'paid').length,
    outstanding: invoices
      .filter((i) => unpaidStatuses.includes(i.status))
      .reduce((acc, i) => acc + outstandingBalance(i), 0),
  };

  const cards = [
    { key: 'total', label: 'Total Invoices', value: stats.total, Icon: Receipt, tint: 'bg-blue-100 text-blue-600' },
    { key: 'overdue', label: 'Overdue', value: stats.overdue, Icon: AlertTriangle, tint: 'bg-rose-100 text-rose-600' },
    { key: 'processing', label: 'Processing', value: stats.processing, Icon: RefreshCw, tint: 'bg-sky-100 text-sky-600' },
    { key: 'paid', label: 'Paid', value: stats.paid, Icon: CheckCircle, tint: 'bg-green-100 text-green-600' },
    { key: 'outstanding', label: 'Outstanding (A/R)', value: `${cs()}${stats.outstanding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, Icon: DollarSign, tint: 'bg-red-100 text-red-600' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map(({ key, label, value, Icon, tint }) => (
        <Card key={key} data-testid={`invoice-stat-${key}`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${tint.split(' ')[0]}`}>
                <Icon className={`h-5 w-5 ${tint.split(' ')[1]}`} />
              </div>
              <div>
                {loading ? (
                  <div
                    className="h-7 w-20 rounded bg-slate-200 animate-pulse"
                    data-testid={`invoice-stat-${key}-loading`}
                  />
                ) : (
                  <p className="text-2xl font-bold" data-testid={`invoice-stat-${key}-value`}>{value}</p>
                )}
                <p className="text-sm text-slate-500">{label}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
